import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { AppDefinition } from "@acruxsoft/platform-shared";
import { requestAppLicense } from "../lib/callables";
import { useAuthCtx } from "../lib/auth-provider";
import { readContactProfile } from "../lib/profile";

interface Props {
  app: AppDefinition;
  /** Prefill from an earlier request, so asking again isn't retyping. */
  initialCompany?: string;
  initialMessage?: string;
  onClose: () => void;
  /** Called after the request is stored. The tile flips to "requested" from the live listener. */
  onSent: () => void;
}

/**
 * Asks for a license to one app. Name and email come from the session — the
 * admin gets them in the notification. Company and phone are prefilled from
 * what the user saved after signup, and stay editable: this is the last
 * chance to correct how the admin will reach them.
 */
export function LicenseRequestDialog({ app, initialCompany = "", initialMessage = "", onClose, onSent }: Props) {
  const { t } = useTranslation();
  const { user } = useAuthCtx();
  const [company, setCompany] = useState(initialCompany);
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState(initialMessage);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const firstField = useRef<HTMLInputElement>(null);

  useEffect(() => {
    firstField.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [busy, onClose]);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    readContactProfile(user.uid)
      .then((p) => {
        if (cancelled) return;
        setCompany((c) => c || p.company || "");
        setPhone((v) => v || p.phone || "");
      })
      .catch((e) => console.warn("[license-request] could not prefill the profile", e));
    return () => {
      cancelled = true;
    };
  }, [user]);

  const appName = t(`home.apps.${app.appId}.name`);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (!company.trim()) {
      setError(t("home.request.companyRequired"));
      return;
    }
    if (!phone.trim()) {
      setError(t("home.request.phoneRequired"));
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await requestAppLicense({
        appId: app.appId,
        company: company.trim(),
        phone: phone.trim(),
        message: message.trim() || undefined
      });
      onSent();
    } catch (err: unknown) {
      console.error("[license-request] failed", err);
      const msg = err instanceof Error ? err.message : "";
      setError(
        msg.includes("already-licensed")
          ? t("home.request.alreadyLicensed")
          : msg.includes("email-not-verified")
            ? t("home.request.verifyFirst")
            : t("home.request.error")
      );
      setBusy(false);
    }
  }

  return (
    <div className="acx__modal-backdrop" onMouseDown={(e) => e.target === e.currentTarget && !busy && onClose()}>
      <div className="acx__modal" role="dialog" aria-modal="true" aria-labelledby="acx-license-request-title">
        <form onSubmit={submit} className="acx__stack acx__stack--lg">
          <header className="acx__stack acx__stack--sm">
            <span className="acx__card-eyebrow">{t("home.request.eyebrow")}</span>
            <h2 id="acx-license-request-title" className="acx__card-title" style={{ fontSize: "var(--acx-fs-lg)" }}>
              {t("home.request.title", { app: appName })}
            </h2>
            <p className="acx__home-plan-desc">{t("home.request.body")}</p>
          </header>

          <div className="acx__field">
            <label htmlFor="acx-license-company" className="acx__label">{t("home.request.companyLabel")}</label>
            <input
              id="acx-license-company"
              ref={firstField}
              className="acx__input"
              type="text"
              value={company}
              onChange={(e) => setCompany(e.target.value)}
              maxLength={120}
              autoComplete="organization"
              disabled={busy}
              required
            />
          </div>

          <div className="acx__field">
            <label htmlFor="acx-license-phone" className="acx__label">{t("home.request.phoneLabel")}</label>
            <input
              id="acx-license-phone"
              className="acx__input"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              maxLength={40}
              autoComplete="tel"
              disabled={busy}
              required
            />
          </div>

          <div className="acx__field">
            <label htmlFor="acx-license-message" className="acx__label">{t("home.request.messageLabel")}</label>
            <textarea
              id="acx-license-message"
              className="acx__input acx__textarea"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              maxLength={2000}
              rows={4}
              placeholder={t("home.request.messagePlaceholder")}
              disabled={busy}
            />
          </div>

          {error && <div className="acx__alert acx__alert--danger" role="alert">{error}</div>}

          <div className="acx__row" style={{ justifyContent: "flex-end", gap: "var(--acx-s-3)" }}>
            <button type="button" className="acx__btn acx__btn--ghost" onClick={onClose} disabled={busy}>
              {t("home.request.cancel")}
            </button>
            <button type="submit" className="acx__btn acx__btn--primary" disabled={busy}>
              {busy ? t("home.request.sending") : t("home.request.send")}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
